/**
 * AutoLoginPage.jsx  —  /auto-login
 *
 * Public rota. ?ticket= query'sini alır, POST /auth/impersonate-exchange çağırır,
 * dönen token'ı SEKMEYE OZEL sessionStorage'a yazar (localStorage'a dokunmaz) ve
 * öğrenci paneline yönlendirir. Boylece impersonation izole olur.
 *
 * Hata durumunda (ticket_expired / ticket_already_used) Türkçe hata ekranı.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { setSessionSession, getToken } from '../services/authToken';

// Guvenlik agi: exchange beklenenden uzun surerse (ag/sema sorunu) sessiz
// "Giris yapiliyor..." ekraninda asili kalmayalim.
const LOADING_TIMEOUT_MS = 12000;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error messages keyed by error_code
const ERROR_MESSAGES = {
  ticket_expired: {
    tr: 'Bu giriş bağlantısının süresi dolmuş. Lütfen yeni bir bağlantı talep edin.',
    en: 'This login link has expired. Please request a new one.',
  },
  ticket_already_used: {
    tr: 'Bu giriş bağlantısı daha önce kullanılmış. Her bağlantı yalnızca bir kez kullanılabilir.',
    en: 'This login link has already been used. Each link can only be used once.',
  },
};

function getErrMsg(err, lang) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const code = detail.error_code;
    if (code && ERROR_MESSAGES[code]) {
      return ERROR_MESSAGES[code][lang] || ERROR_MESSAGES[code].tr;
    }
    return detail[`message_${lang}`] || detail.message_tr || detail.message_en || 'Hata oluştu';
  }
  if (typeof detail === 'string') return detail;
  return lang === 'tr' ? 'Giriş yapılırken bir hata oluştu.' : 'An error occurred during login.';
}

export default function AutoLoginPage() {
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const ticket = searchParams.get('ticket');
  // StrictMode cift-mount korumasi: ticket tek-kullanimlik oldugundan exchange
  // SADECE bir kez tetiklenmeli. exchangedRef ile ikinci mount'ta fetch atlanir.
  //
  // KRITIK (P0 fix): Onceki surumde cleanup `cancelled=true` yapip exchange'in
  // SUCCESS yolunu (token persist + redirect) iptal ediyordu. StrictMode'da
  // mount->cleanup->remount sirasinda ilk doExchange'in 200 yaniti geldiginde
  // `cancelled` zaten true oldugu icin token sessionStorage'a YAZILMADAN return
  // ediliyordu; ikinci mount ise exchangedRef yuzunden exchange'i hic yapmiyordu
  // -> sessiz olum (sayfa "Giris yapiliyor..."da asili, log yok, redirect yok).
  //
  // Cozum: token tek-kullanimlik bir kaynak; exchange'den DONDUYSE onu ATMAK
  // veri kaybidir. Bu yuzden persist + redirect'i `cancelled`/unmount'tan
  // BAGIMSIZ yapiyoruz. setState'ler unmount sonrasi no-op olur (React 18'de
  // sessiz), redirect zaten sayfayi terk eder.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (!ticket) {
      setErrorMsg(tr('Geçersiz bağlantı — bilet parametresi eksik.', 'Invalid link — ticket parameter missing.'));
      setStatus('error');
      return;
    }

    // Ikinci mount (StrictMode) veya yeniden render: exchange'i tekrar tetikleme.
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    // Sessiz takilma korumasi: timeout dolarsa (ve token hala yoksa) hata ekranina dus.
    const timeoutId = setTimeout(() => {
      // success'e gectiyse (token yazildi, redirect bekleniyor) dokunma.
      if (getToken()) return;
       
      console.error('[auto-login] exchange zaman asimi — yanit gelmedi veya token yazilmadi.');
      setErrorMsg(tr(
        'Giris islemi zaman asimina ugradi. Lutfen baglantiyi yeniden deneyin.',
        'Login timed out. Please try the link again.',
      ));
      setStatus('error');
    }, LOADING_TIMEOUT_MS);

    const doExchange = async () => {
      try {
        const res = await axios.post(`${API}/auth/impersonate-exchange`, { ticket });

        // Backend semasi (routes/impersonation.py exchange_impersonate_ticket):
        //   { token, user_id, user, impersonated_by? }  — refresh_token YOK.
        // Anahtar adi "token" (access_token DEGIL). undefined refresh sorun degil.
        const token = res?.data?.token;
        const refresh_token = res?.data?.refresh_token; // backend gondermez; ileri-uyumluluk

        if (!token || typeof token !== 'string') {
           
          console.error('[auto-login] exchange yaniti beklenen semada degil — token yok:', res?.data);
          clearTimeout(timeoutId);
          setErrorMsg(tr(
            'Giris yaniti beklenmedik bicimde dondu. Lutfen tekrar deneyin.',
            'Login response was malformed. Please try again.',
          ));
          setStatus('error');
          return;
        }

        // SEKMEYE OZEL oturum: token'i SADECE sessionStorage'a yaz. localStorage'a
        // DOKUNMA -> admin'in kendi sekmesi/oturumu etkilenmez, 'storage' event'i
        // tetiklenmez. Boylece admin sekmesi admin KALIR, bu sekme ogrenci olur.
        // NOT: cleanup/unmount durumuna BAKMADAN yaziyoruz — token tek-kullanimlik,
        // atmak veri kaybi. StrictMode cift-mount'ta bile token kalici olmali.
        setSessionSession(token, refresh_token);

        // Yazma gercekten oldu mu? (private mode / quota hatasi sessiz kalmasin)
        if (!getToken()) {
           
          console.error('[auto-login] token sessionStorage write basarisiz.');
          clearTimeout(timeoutId);
          setErrorMsg(tr(
            'Oturum bu tarayicida saklanamadi. Gizli sekme/depolama ayarlarini kontrol edin.',
            'Session could not be stored in this browser. Check private mode / storage settings.',
          ));
          setStatus('error');
          return;
        }

        clearTimeout(timeoutId);
        setStatus('success');

        // Hard-navigate: AuthContext yeniden mount olur, checkAuth() sessionStorage
        // token'ini okuyup /auth/me ile kullaniciyi yukler. Ogrenci -> /dashboard.
        setTimeout(() => {
          window.location.replace('/dashboard');
        }, 800);
      } catch (err) {
        clearTimeout(timeoutId);
         
        console.error('[auto-login] exchange hatasi:', err?.response?.status, err?.response?.data || err?.message);
        setErrorMsg(getErrMsg(err, lang));
        setStatus('error');
      }
    };

    doExchange();

    // cleanup: sadece timeout'u temizle. exchange'in success yolunu IPTAL ETME.
    return () => { clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  return (
    <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10">
      <motion.div
        className="bg-white rounded-2xl p-8 max-w-sm w-full text-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ border: '2px solid var(--duo-stroke)' }}
      >
        {status === 'loading' && (
          <>
            <Loader2 size={40} className="animate-spin text-[var(--brand-primary)] mx-auto mb-4" />
            <h2 className="font-duo-display text-xl text-[var(--duo-text)] mb-2">
              {tr('Giriş yapılıyor...', 'Logging in...')}
            </h2>
            <p className="text-sm text-[var(--duo-text-soft)] font-medium">
              {tr('Lütfen bekleyin.', 'Please wait.')}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'var(--duo-green)', boxShadow: '0 4px 0 0 var(--duo-green-dark)' }}
            >
              <CheckCircle2 size={28} strokeWidth={2.5} className="text-white" />
            </div>
            <h2 className="font-duo-display text-xl text-[var(--duo-text)] mb-2">
              {tr('Giriş başarılı!', 'Login successful!')}
            </h2>
            <p className="text-sm text-[var(--duo-text-soft)] font-medium">
              {tr('Yönlendiriliyorsunuz...', 'Redirecting...')}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} className="text-rose-500" />
            </div>
            <h2 className="font-duo-display text-xl text-[var(--duo-text)] mb-3">
              {tr('Giriş Başarısız', 'Login Failed')}
            </h2>
            <p className="text-sm text-[var(--duo-error-dark)] font-semibold mb-6">
              {errorMsg}
            </p>
            <Link
              to="/login"
              className="btn-duo-secondary inline-flex items-center justify-center w-full"
            >
              {tr('Giriş Sayfasına Dön', 'Back to Login')}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
